import React, { useState, useEffect } from 'react';
import { useRestaurants } from '@/hooks/useRestaurants';
import { QuoteGenerator } from './QuoteGenerator';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Building2, Loader2, ChevronRight } from 'lucide-react';

export const QuoteGeneratorV2: React.FC = () => {
  const { data: clients, isLoading } = useRestaurants();
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [showGenerator, setShowGenerator] = useState(false);
  const [prefillData, setPrefillData] = useState<any>(null);

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    const client = clients?.find(c => c.id === clientId);
    
    if (client) {
      const primaryContact = client.primary_contact as any;
      setPrefillData({
        companyName: client.name,
        contactName: primaryContact?.name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.location || '',
        siret: primaryContact?.siret || ''
      });
      setShowGenerator(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-stratos-gold animate-spin" />
      </div>
    );
  }

  if (!showGenerator) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Générer un Devis</CardTitle>
          <CardDescription>
            Sélectionnez un client pour pré-remplir les informations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="client">Client</Label>
            <select
              id="client"
              value={selectedClientId || ''}
              onChange={(e) => e.target.value && handleClientSelect(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm mt-1"
            >
              <option value="">Sélectionnez un client</option>
              {clients?.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} - {client.location || 'Non renseigné'}
                </option>
              ))}
            </select>
          </div>

          {clients && clients.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                Aucun client disponible. Ajoutez d'abord des clients dans la page d'accueil.
              </p>
            </div>
          )}

          <div className="pt-4 border-t">
            <Button
              onClick={() => setShowGenerator(true)}
              variant="outline"
              className="w-full"
            >
              Créer un devis sans client pré-sélectionné
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          {selectedClientId && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Building2 className="w-4 h-4" />
              <span>
                Client sélectionné:{' '}
                <strong>{clients?.find(c => c.id === selectedClientId)?.name}</strong>
              </span>
            </div>
          )}
        </div>
        <Button
          onClick={() => {
            setShowGenerator(false);
            setSelectedClientId(null);
            setPrefillData(null);
          }}
          variant="outline"
          size="sm"
        >
          ← Changer de client
        </Button>
      </div>

      <QuoteGenerator initialData={prefillData} />
    </div>
  );
};

